/**
 * Shared IPD design tokens — use across admission, billing, inventory, etc.
 */

export const IPD_COLORS = {
  primary: "#1A2B4B",
  primaryDark: "#0D1B2A",
  primaryLight: "#3D5A80",
  pageBg: "#F8FAFC",
  cardBg: "#FFFFFF",
  border: "#E2E8F0",
  borderHover: "#CBD5E1",
  textPrimary: "#1A2B4B",
  // The text ramp is contrast-bound, not taste-bound. Against the #FFFFFF card
  // these measure 14.08:1 / 5.75:1 / 4.83:1, so all three clear WCAG AA (4.5:1)
  // for body text at any size. They were #64748B / #94A3B8, which put muted at
  // 2.56:1 — unreadable, and it carried real text (vitals labels at 10.5px, the
  // "Not triaged" chip, "No bed"/"Unassigned"). Secondary moved with it because
  // it sat at 4.76:1: leaving it there would have made muted the DARKER of the
  // two and inverted the hierarchy. Lightening either one re-breaks AA.
  textSecondary: "#5A6779",
  textMuted: "#6B7280",
  panelHeaderBg: "#F8FAFC",
  selectedBg: "#E1F5FE",
  selectedBorder: "#0288D1",
  chipInfoBg: "#E1F5FE",
  // 6.59:1 on chipInfoBg. Was #0288D1 (3.43:1), which failed AA on the OT
  // board's status chips where it renders at 11px/700.
  chipInfoText: "#01579B",
  white: "#FFFFFF",
  success: { bg: "#E6F4EA", text: "#1E7E34", border: "#A5D6A7" },
  warning: { bg: "#FFF8E1", text: "#B45309", border: "#FFE082" },
  error: { bg: "#FDECEA", text: "#B91C1C", border: "#EF9A9A" },
  // info.text is 6.59:1 on info.bg (was #0288D1 at 3.43:1). `selectedBorder`
  // above keeps #0288D1 deliberately: as a border it answers to SC 1.4.11's
  // 3:1, which it passes, and darkening it would lose the selected-state hue.
  info: { bg: "#E1F5FE", text: "#01579B", border: "#81D4FA" },
  shadow: {
    card: "0 1px 3px rgba(15, 23, 42, 0.08)",
    cardHover: "0 4px 12px rgba(15, 23, 42, 0.12)",
    selected: "0 4px 16px rgba(26, 43, 75, 0.18)",
    field: "0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.06)",
    fieldHover: "0 2px 6px rgba(15, 23, 42, 0.1)",
    fieldFocus: "0 4px 12px rgba(15, 23, 42, 0.12)",
    menu: "0 4px 16px rgba(15, 23, 42, 0.14)",
    button: "0 1px 2px rgba(13, 35, 63, 0.12)",
    buttonHover: "0 2px 8px rgba(13, 35, 63, 0.18)",
  },
  scrollbarThumb: "#C5CDD4",
  scrollbarThumbHover: "#9AA5B0",
  toggleTrackBg: "#F1F3F5",
  sidebar: {
    bgGradientEnd: "#0A1A2E",
    text: "#B8CFE0",
    label: "#6A94B0",
    border: "rgba(255,255,255,0.08)",
  },
};

/** @deprecated Use IPD_COLORS — kept for existing admission imports */
export const ADMISSION_COLORS = IPD_COLORS;

export const IPD_SHAPE = {
  radiusSm: 6,
  radiusMd: 8,
  radiusLg: 10,
  radiusXl: 12,
};

export const IPD_SPACING = {
  page: 2,
  section: 2,
  card: 2,
  fieldGap: 2,
};

export const IPD_TYPOGRAPHY = {
  pageTitle: { fontWeight: 700, fontSize: "1.25rem", color: IPD_COLORS.textPrimary },
  sectionTitle: { fontWeight: 600, fontSize: "0.875rem", color: IPD_COLORS.textPrimary },
  panelTitle: { fontWeight: 700, fontSize: "1.125rem", color: IPD_COLORS.textPrimary },
  body: { fontSize: "0.875rem", color: IPD_COLORS.textPrimary },
  caption: { fontSize: "0.75rem", color: IPD_COLORS.textSecondary },
  label: { fontSize: "0.8125rem", fontWeight: 600, color: IPD_COLORS.textPrimary },
};

/** Page shell — full-height light background */
export const ipdPageSx = {
  minHeight: "100%",
  bgcolor: IPD_COLORS.pageBg,
};

/** Standard elevated card / panel */
export const ipdCardSx = {
  bgcolor: IPD_COLORS.cardBg,
  borderRadius: `${IPD_SHAPE.radiusMd}px`,
  border: `1px solid ${IPD_COLORS.border}`,
  boxShadow: IPD_COLORS.shadow.card,
};

/** Accordion / section header row */
export const ipdPanelHeaderSx = {
  bgcolor: IPD_COLORS.panelHeaderBg,
  borderRadius: `${IPD_SHAPE.radiusMd}px`,
  minHeight: 48,
  px: 2,
};

/** Selected / active list item or tile */
export const ipdSelectedSx = {
  bgcolor: IPD_COLORS.selectedBg,
  border: `1px solid ${IPD_COLORS.selectedBorder}`,
  boxShadow: IPD_COLORS.shadow.selected,
};

/** Semantic chip styles — pass "success" | "warning" | "error" | "info" */
export function ipdSemanticSx(variant) {
  const token = IPD_COLORS[variant];
  if (!token) return {};
  return {
    bgcolor: token.bg,
    color: token.text,
    border: `1px solid ${token.border}`,
    fontWeight: 600,
  };
}

export const formRowBg = IPD_COLORS.chipInfoBg;

/** Padded highlighted form-row container */
export const formRowSx = {
  p: 2,
  borderRadius: `${IPD_SHAPE.radiusMd}px`,
  backgroundColor: formRowBg,
  alignItems: "center",
};

/** MUI Switch checked-state colors */
export const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: IPD_COLORS.primary },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: IPD_COLORS.primary },
};
